<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { useAuth } from '@/composables/useAuth'
import { useImportacao } from '@/composables/useImportacao'
import { getCadastroConfig } from '@/config/cadastros'
import api from '@/services/api'
import { buscarCategoriasProduto } from '@/services/importacaoService'
import CadastroSelector from '@/components/CadastroSelector.vue'
import FileUploader from '@/components/FileUploader.vue'
import ImportProgress from '@/components/ImportProgress.vue'
import ImportSummary from '@/components/ImportSummary.vue'
import GridCorrecao from '@/components/GridCorrecao.vue'
import type { TipoCadastro, ItemCorrecao } from '@/types'

const { isAuthenticated, authError, initAuth } = useAuth()
const {
  status,
  arquivo,
  tipoCadastro,
  processamento,
  erro,
  processados,
  totalParaProcessar,
  jobId,
  itensFalha,
  selecionarTipo,
  uploadArquivo,
  removerArquivo,
  processarImportacao,
  corrigirEReprocessar,
  retomarJobAtual,
  novaImportacao
} = useImportacao()

// Estados locais
const selectedTipo = ref<TipoCadastro | null>(null)
const uploadError = ref<string | null>(null)
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')
const baixandoTemplate = ref(false)

// Computed
const podeProcessar = computed(() => {
  return (
    selectedTipo.value !== null &&
    arquivo.value !== null &&
    status.value === 'validated'
  )
})

const isProcessing = computed(() => {
  return ['uploading', 'validating', 'queued', 'processing', 'importing'].includes(status.value)
})

// Há registros que falharam (inválidos/erros/duplicados) para mostrar no grid
const temFalhas = computed(() => itensFalha.value.length > 0)

const selectedConfig = computed(() => {
  if (!selectedTipo.value) return null
  return getCadastroConfig(selectedTipo.value)
})

// Observações por campo (ex.: mod_trib só obrigatório p/ CNPJ) exibidas nos requisitos
const notasCamposVisiveis = computed(() => {
  const notas = selectedConfig.value?.notasCampos
  if (!notas) return []
  return Object.entries(notas).map(([campo, texto]) => ({ campo, texto }))
})

const getTemplateUrl = computed(() => {
  if (!selectedTipo.value) return '#'
  const config = getCadastroConfig(selectedTipo.value)
  return `/templates/${config.templateFile}`
})

// Handlers
const onTipoSelected = (tipo: TipoCadastro) => {
  selectedTipo.value = tipo
  selecionarTipo(tipo)
  uploadError.value = null
}

const onFileSelected = async (file: File) => {
  uploadError.value = null
  await uploadArquivo(file)
}

const onFileError = (message: string) => {
  uploadError.value = message
  showNotification(message, 'error')
}

const onFileRemoved = () => {
  removerArquivo()
  uploadError.value = null
}

const iniciarImportacao = async () => {
  await processarImportacao()
}

const handleReprocessar = async (itens: ItemCorrecao[]) => {
  await corrigirEReprocessar(itens)
}

const handleNovaImportacao = async () => {
  selectedTipo.value = null
  await novaImportacao()
  showNotification('Pronto para nova importação', 'info')
}

const exportarRelatorio = () => {
  if (!processamento.value) return

  const wb = XLSX.utils.book_new()
  const resultado = processamento.value

  // Aba 1: Resumo
  const resumoData = [
    ['RELATÓRIO DE IMPORTAÇÃO'],
    [''],
    ['Data/Hora', new Date().toLocaleString('pt-BR')],
    ['Tipo de Cadastro', selectedTipo.value],
    ['Arquivo', resultado.info?.nomeArquivo || '-'],
    [''],
    ['RESUMO'],
    ['Total de Registros', resultado.total],
    ['Inseridos com Sucesso', resultado.inseridos],
    ['Duplicados', resultado.duplicados],
    ['Erros', resultado.erros],
    ['Taxa de Sucesso', `${resultado.total > 0 ? Math.round((resultado.inseridos / resultado.total) * 100) : 0}%`]
  ]
  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData)
  wsResumo['!cols'] = [{ wch: 25 }, { wch: 40 }]
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo')

  // Aba 2: Registros Inválidos (se houver)
  if (resultado.registrosInvalidos && resultado.registrosInvalidos.length > 0) {
    // Obter colunas dos dados originais do primeiro registro
    const colunasOriginais = Object.keys(resultado.registrosInvalidos[0].dadosOriginais || {})
    const invalidosHeader = ['Linha', 'Erro', ...colunasOriginais]
    const invalidosData: (string | number | unknown)[][] = []

    resultado.registrosInvalidos.forEach(reg => {
      const dadosOriginais = colunasOriginais.map(col => reg.dadosOriginais?.[col] ?? '')

      if (reg.erros && reg.erros.length > 0) {
        // Concatenar todos os erros em uma string
        const errosTexto = reg.erros.map(e => `${e.campo}: ${e.mensagem}`).join('; ')
        invalidosData.push([reg.linha, errosTexto, ...dadosOriginais])
      } else if (reg.motivo) {
        // Fallback para estrutura antiga
        invalidosData.push([reg.linha, reg.motivo, ...dadosOriginais])
      }
    })

    const wsInvalidos = XLSX.utils.aoa_to_sheet([invalidosHeader, ...invalidosData])
    wsInvalidos['!cols'] = [{ wch: 8 }, { wch: 50 }, ...colunasOriginais.map(() => ({ wch: 18 }))]
    XLSX.utils.book_append_sheet(wb, wsInvalidos, 'Registros Inválidos')
  }

  // Aba 3: Registros Duplicados (se houver)
  if (resultado.registrosDuplicados && resultado.registrosDuplicados.length > 0) {
    const duplicadosHeader = ['Linha', 'Motivo', ...Object.keys(resultado.registrosDuplicados[0].dadosOriginais || {})]
    const duplicadosData = resultado.registrosDuplicados.map(reg => [
      reg.linha,
      reg.motivo,
      ...Object.values(reg.dadosOriginais || {})
    ])
    const wsDuplicados = XLSX.utils.aoa_to_sheet([duplicadosHeader, ...duplicadosData])
    wsDuplicados['!cols'] = duplicadosHeader.map(() => ({ wch: 20 }))
    XLSX.utils.book_append_sheet(wb, wsDuplicados, 'Registros Duplicados')
  }

  // Aba 4: Erros de Inserção (se houver)
  if (resultado.errosInsercao && resultado.errosInsercao.length > 0) {
    const errosHeader = ['Cliente/Fornecedor/Credor/Ativo', 'CPF/CNPJ ou Placa', 'Erro']
    const errosData = resultado.errosInsercao.map(erro => [
      erro.cliente || erro.fornecedor || erro.credor || erro.ativo || erro.ativo_codigo || '',
      erro.cpf_cnpj || erro.ativo_placa || '',
      erro.erro
    ])
    const wsErros = XLSX.utils.aoa_to_sheet([errosHeader, ...errosData])
    wsErros['!cols'] = [{ wch: 40 }, { wch: 20 }, { wch: 60 }]
    XLSX.utils.book_append_sheet(wb, wsErros, 'Erros de Inserção')
  }

  // Gerar arquivo e fazer download
  const nomeArquivo = `relatorio_importacao_${selectedTipo.value}_${new Date().toISOString().slice(0, 10)}.xlsx`
  XLSX.writeFile(wb, nomeArquivo)

  showNotification('Relatório exportado com sucesso', 'success')
}

const baixarTemplateAtivos = async () => {
  baixandoTemplate.value = true
  try {
    const [resAtivo, resVeiculo, resCacamba] = await Promise.all([
      api.get('/tipo-ativo'),
      api.get('/tipo-veiculo'),
      api.get('/tipo-cacamba'),
    ])

    const tiposAtivo: string[] = resAtivo.data.map((t: any) => t.descricao).filter(Boolean)
    const tiposVeiculo: string[] = resVeiculo.data.map((t: any) => t.descricao).filter(Boolean)
    const tiposCacamba: string[] = resCacamba.data.map((t: any) => t.descricao).filter(Boolean)

    const workbook = new ExcelJS.Workbook()

    // A importacao le a primeira aba do arquivo. Mantenha a aba de dados antes das listas ocultas.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ws = workbook.addWorksheet('Importacao') as any

    const wsListas = workbook.addWorksheet('Listas', { state: 'veryHidden' })
    const maxRows = Math.max(tiposAtivo.length, tiposVeiculo.length, tiposCacamba.length)
    for (let i = 0; i < maxRows; i++) {
      wsListas.addRow([tiposAtivo[i] ?? '', tiposVeiculo[i] ?? '', tiposCacamba[i] ?? ''])
    }

    const campos = [
      'ativo_codigo', 'ativo_descricao', 'ativo_local', 'ativo_tipo', 'ativo_veicprop',
      'ativo_valativo', 'ativo_placa', 'ativo_tara', 'ativo_capacidade', 'ativo_mediapadrao',
      'ativo_licenciamento', 'ativo_marca', 'ativo_modelo', 'ativo_ano', 'ativo_cor',
      'ativo_renavan', 'ativo_chassis', 'ativo_tipoconsumo', 'ativo_tipoveiculo', 'ativo_tipocacamba'
    ]
    const obrigatorios = ['ativo_codigo', 'ativo_descricao', 'ativo_tipo', 'ativo_veicprop']

    const headerRow = ws.addRow(campos)
    headerRow.eachCell((cell: any, colIdx: number) => {
      const campo = campos[colIdx - 1]
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: obrigatorios.includes(campo) ? 'FFE53935' : 'FF43A047' }
      }
      cell.alignment = { horizontal: 'center' }
    })
    ws.getRow(1).height = 20
    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 1, activeCell: 'A2' }]
    campos.forEach((_, i) => { ws.getColumn(i + 1).width = 20 })

    // Linhas de exemplo (fundo amarelo claro — usuário pode excluir antes de importar)
    const normEx = (s: string) => [...s.toUpperCase().normalize('NFD')].filter(c => { const cp = c.charCodeAt(0); return cp < 0x300 || cp > 0x36F }).join('')
    const tipoVeiculoEx = tiposAtivo.find(t => normEx(t) === 'VEICULO') ?? tiposAtivo[0] ?? 'VEICULO'
    const tipoCacambaEx = tiposAtivo.find(t => normEx(t) === 'CACAMBA')
    const tipoSemRegraCondicionalEx = tiposAtivo.find(t => !['VEICULO', 'CACAMBA'].includes(normEx(t)))
    const tipoSegundoEx = tipoSemRegraCondicionalEx ?? (tipoCacambaEx && tiposCacamba.length > 0 ? tipoCacambaEx : tipoVeiculoEx)
    const segundoTipoEhVeiculo = normEx(tipoSegundoEx) === 'VEICULO'
    const segundoTipoEhCacamba = normEx(tipoSegundoEx) === 'CACAMBA'

    const exemplos = [
      [
        'V001', 'CAMINHAO EXEMPLO', 'PATIO', tipoVeiculoEx, 'TRUE',
        '150000.00', 'ABC1234', '8.500', '15.000', '8.500',
        '01/01/2026', 'MERCEDES', 'ATEGO 2430', '2020', 'BRANCO',
        '12345678901', '9BM384075LB123456', '1', tiposVeiculo[0] ?? '', '',
      ],
      [
        'M001', segundoTipoEhVeiculo ? 'VEICULO APOIO EXEMPLO' : segundoTipoEhCacamba ? 'CACAMBA EXEMPLO' : 'MAQUINA EXEMPLO', 'OFICINA', tipoSegundoEx, 'FALSE',
        '50000.00', segundoTipoEhVeiculo ? 'DEF5678' : '', '', '', '',
        '', 'CATERPILLAR', 'CAT 320', '2018', 'AMARELO',
        '', '', '', segundoTipoEhVeiculo ? (tiposVeiculo[0] ?? '') : '', segundoTipoEhCacamba ? (tiposCacamba[0] ?? '') : '',
      ],
    ]

    exemplos.forEach(dados => {
      const row = ws.addRow(dados)
      row.eachCell({ includeEmpty: true }, (cell: any) => {
        cell.font = { italic: true }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF9C' } }
      })
    })

    const colLetter = (n: number) => String.fromCharCode(64 + n)
    const iAtivo = campos.indexOf('ativo_tipo') + 1
    const iVeiculo = campos.indexOf('ativo_tipoveiculo') + 1
    const iCacamba = campos.indexOf('ativo_tipocacamba') + 1

    ws.dataValidations.add(`${colLetter(iAtivo)}2:${colLetter(iAtivo)}10000`, {
      type: 'list',
      allowBlank: false,
      formulae: [`Listas!$A$1:$A$${tiposAtivo.length}`],
      showErrorMessage: true,
      errorStyle: 'stop',
      error: 'Selecione um valor válido da lista.',
      errorTitle: 'Tipo de Ativo Inválido',
    })

    ws.dataValidations.add(`${colLetter(iVeiculo)}2:${colLetter(iVeiculo)}10000`, {
      type: 'list',
      allowBlank: true,
      formulae: [`Listas!$B$1:$B$${tiposVeiculo.length}`],
      showErrorMessage: true,
      errorStyle: 'stop',
      error: 'Selecione um valor válido da lista.',
      errorTitle: 'Tipo de Veículo Inválido',
    })

    if (tiposCacamba.length > 0) {
      ws.dataValidations.add(`${colLetter(iCacamba)}2:${colLetter(iCacamba)}10000`, {
        type: 'list',
        allowBlank: true,
        formulae: [`Listas!$C$1:$C$${tiposCacamba.length}`],
        showErrorMessage: true,
        errorStyle: 'stop',
        error: 'Selecione um valor válido da lista.',
        errorTitle: 'Tipo de Caçamba Inválido',
      })
    }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'template_ativos.xlsx'
    link.click()
    URL.revokeObjectURL(url)

    showNotification('Template gerado com sucesso', 'success')
  } catch {
    showNotification('Erro ao gerar template de ativos', 'error')
  } finally {
    baixandoTemplate.value = false
  }
}

const baixarTemplateEstatico = () => {
  if (!selectedTipo.value) return
  const config = getCadastroConfig(selectedTipo.value)
  const link = document.createElement('a')
  link.href = `/templates/${config.templateFile}`
  link.download = config.templateFile
  link.click()
}

const baixarTemplateProdutos = async () => {
  baixandoTemplate.value = true
  try {
    const resultado = await buscarCategoriasProduto()

    if (!resultado || resultado.items.length === 0) {
      baixarTemplateEstatico()
      showNotification('Categorias indisponíveis — baixando template padrão', 'warning')
      return
    }

    const config = getCadastroConfig('produtos')
    const campos = config.todosCampos
    const obrigatorios = config.camposObrigatorios

    const workbook = new ExcelJS.Workbook()
    workbook.creator = 'App Cadastro Importer'
    workbook.created = new Date()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ws = workbook.addWorksheet('Dados') as any

    const wsListas = workbook.addWorksheet('Listas', { state: 'veryHidden' })
    resultado.items.forEach((cat) => {
      wsListas.addRow([cat.codcat])
    })

    const headerRow = ws.addRow(campos)
    headerRow.eachCell((cell: any, colIdx: number) => {
      const campo = campos[colIdx - 1]
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: obrigatorios.includes(campo) ? 'FFE53935' : 'FF4CAF50' }
      }
      cell.alignment = { horizontal: 'center', vertical: 'middle' }
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
    })
    ws.getRow(1).height = 20
    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 1, activeCell: 'A2' }]

    const textColumns = new Set(['codpro', 'codcat', 'ncm', 'tp_prod'])
    campos.forEach((campo, i) => {
      const col = ws.getColumn(i + 1)
      col.width = Math.max(campo.length + 4, 15)
      if (textColumns.has(campo)) col.numFmt = '@'
    })

    // Linhas de exemplo (amarelo claro — usuário deve remover antes de importar)
    const primeiroCodcat = resultado.items[0]?.codcat ?? ''
    const exemplos = [
      ['ALLA', 'ALUMINIO LATINHA', 'S', 'TN', primeiroCodcat, '', '76012000', '01', 'FALSE'],
      ['ALLA', 'ALUMINIO LATINHA', 'F', 'TN', primeiroCodcat, '', '76012000', '01', 'FALSE']
    ]
    exemplos.forEach((dados) => {
      const row = ws.addRow(dados)
      row.eachCell({ includeEmpty: true }, (cell: any) => {
        cell.font = { italic: true }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF9C4' } }
      })
    })

    const colLetter = (n: number) => String.fromCharCode(64 + n)
    const iCodcat = campos.indexOf('codcat') + 1
    const iSubcod = campos.indexOf('subcod') + 1
    const iTpProd = campos.indexOf('tp_prod') + 1
    const iDiverso = campos.indexOf('diverso') + 1

    ws.dataValidations.add(`${colLetter(iCodcat)}2:${colLetter(iCodcat)}1000`, {
      type: 'list',
      allowBlank: false,
      formulae: [`Listas!$A$1:$A$${resultado.items.length}`],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'Categoria inválida',
      error: 'Selecione uma categoria da lista.'
    })

    ws.dataValidations.add(`${colLetter(iSubcod)}2:${colLetter(iSubcod)}1000`, {
      type: 'list',
      allowBlank: true,
      formulae: ['"S,F"'],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'subcod inválido',
      error: 'Selecione S (Solto) ou F (Fardo).'
    })

    ws.dataValidations.add(`${colLetter(iTpProd)}2:${colLetter(iTpProd)}1000`, {
      type: 'list',
      allowBlank: true,
      formulae: ['"00,01,02,03,04,05,06,07,08,09,10,11,12,13,99"'],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'tp_prod inválido',
      error: 'Selecione um valor da lista.'
    })

    ws.dataValidations.add(`${colLetter(iDiverso)}2:${colLetter(iDiverso)}1000`, {
      type: 'list',
      allowBlank: true,
      formulae: ['"TRUE,FALSE"'],
      showErrorMessage: true,
      errorStyle: 'stop',
      errorTitle: 'diverso inválido',
      error: 'Selecione TRUE ou FALSE.'
    })

    // Aba de instruções
    const wsInstrucoes = workbook.addWorksheet('Instruções')
    const instrucoes: string[][] = [
      ['INSTRUÇÕES DE PREENCHIMENTO - PRODUTOS'],
      [''],
      ['LEGENDA DE CORES:'],
      ['- Vermelho: campos OBRIGATÓRIOS'],
      ['- Verde: campos opcionais'],
      [''],
      ['CAMPOS OBRIGATÓRIOS:'],
      [obrigatorios.join(', ')],
      [''],
      ['DICA:'],
      ['- Campos com lista suspensa: clique na célula para ver as opções'],
      [''],
      ['VALIDAÇÕES DO TEMPLATE:'],
      ['- codcat: lista suspensa com as categorias do seu cliente (vinda da API)'],
      ['- subcod: lista suspensa (S = Solto, F = Fardo)'],
      ['- tp_prod: lista suspensa com os tipos de produto'],
      ['- diverso: lista suspensa (TRUE ou FALSE)'],
      [''],
      ['TABELA DE TIPOS DE PRODUTO (tp_prod):'],
      ['00 = MERCADORIA PARA REVENDA'],
      ['01 = MATÉRIA PRIMA'],
      ['02 = EMBALAGEM'],
      ['03 = PRODUTO EM PROCESSO'],
      ['04 = PRODUTO ACABADO'],
      ['05 = SUBPRODUTO'],
      ['06 = PRODUTO INTERMEDIÁRIO'],
      ['07 = MATERIAL DE USO E CONSUMO'],
      ['08 = ATIVO IMOBILIZADO'],
      ['09 = SERVIÇOS'],
      ['10 = OUTROS INSUMOS'],
      ['11 = PRODUTO E.P.I.'],
      ['12 = PNEUS'],
      ['13 = COMBUSTÍVEIS'],
      ['99 = OUTRAS'],
      [''],
      ['CATEGORIAS DISPONÍVEIS (codcat):'],
      ...resultado.items.map((cat) => [`${cat.codcat} = ${cat.descricao}`])
    ]

    if (resultado.truncado) {
      instrucoes.push([''])
      instrucoes.push([
        `> Lista parcial: foram carregadas ${resultado.items.length} de ${resultado.totalItems} categorias. Consulte o sistema se a sua não aparecer.`
      ])
    }

    instrucoes.push(
      [''],
      ['Observações:'],
      ['- Preencha os dados a partir da linha 4 (linhas 2 e 3 são exemplos)'],
      ['- Não altere os nomes das colunas na linha 1'],
      ['- Remova as linhas de exemplo antes de importar'],
      ['- Cada combinação codpro + subcod deve ser única'],
      ['- Produtos duplicados (por código ou descrição) serão ignorados']
    )

    instrucoes.forEach((row, index) => {
      const excelRow = wsInstrucoes.addRow(row)
      if (index === 0) excelRow.font = { bold: true, size: 14 }
    })
    wsInstrucoes.getColumn(1).width = 60

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'template_produtos.xlsx'
    link.click()
    URL.revokeObjectURL(url)

    showNotification('Template gerado com sucesso', 'success')
  } catch {
    baixarTemplateEstatico()
    showNotification('Erro ao gerar template dinâmico — baixando template padrão', 'warning')
  } finally {
    baixandoTemplate.value = false
  }
}

const onBaixarTemplate = () => {
  if (!selectedTipo.value) return
  if (selectedTipo.value === 'ativos') return baixarTemplateAtivos()
  if (selectedTipo.value === 'produtos') return baixarTemplateProdutos()
}

const showNotification = (text: string, color: string = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

onMounted(async () => {
  initAuth()
  if (!isAuthenticated.value) return

  // Retoma uma importação em andamento ou pendente de correção (sobrevive ao F5)
  await retomarJobAtual()
  if (tipoCadastro.value) {
    selectedTipo.value = tipoCadastro.value
  }
})
</script>

<template>
  <v-app class="app-background">
    <v-main>
      <v-container class="py-6" fluid>
        <!-- Header -->
        <div class="header-section mb-6">
          <div class="header-icon">
            <v-icon size="28" color="white">mdi-database-import</v-icon>
          </div>
          <div class="header-text">
            <h1 class="header-title">Importador de Cadastros</h1>
            <p class="header-subtitle">
              Importe dados cadastrais de forma rápida via Excel.
            </p>
          </div>
        </div>

        <!-- Erro de autenticação -->
        <v-alert
          v-if="!isAuthenticated && authError"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          <v-alert-title>Autenticação necessária</v-alert-title>
          {{ authError }}
        </v-alert>

        <!-- Erro geral -->
        <v-alert
          v-if="erro"
          type="error"
          variant="tonal"
          closable
          class="mb-4"
        >
          {{ erro }}
        </v-alert>

        <!-- Conteúdo principal -->
        <template v-if="isAuthenticated">
          <!-- Área do job enfileirado: progresso + resultado + correção -->
          <template v-if="jobId">
            <ImportProgress
              v-if="['queued', 'processing'].includes(status)"
              :processados="processados"
              :total="totalParaProcessar"
              :status="status"
            />

            <ImportSummary
              v-if="status === 'completed'"
              :resultado="processamento"
              @nova-importacao="handleNovaImportacao"
              @exportar-relatorio="exportarRelatorio"
            />

            <GridCorrecao
              v-if="status === 'completed' && selectedTipo && temFalhas"
              :itens="itensFalha"
              :tipo="selectedTipo!"
              @reprocessar="handleReprocessar"
              @descartar="handleNovaImportacao"
            />

            <div v-if="status === 'error'" class="d-flex justify-center mt-4">
              <v-btn color="primary" variant="elevated" prepend-icon="mdi-plus" @click="handleNovaImportacao">
                Nova Importação
              </v-btn>
            </div>
          </template>

          <!-- Fluxo normal (seleção, upload e validação) -->
          <template v-else>
            <v-row>
              <!-- Coluna Esquerda -->
              <v-col cols="12" lg="6">
                <!-- Card 1: Seleção do Tipo -->
                <div class="section-card mb-4">
                  <div class="section-header">
                    <span class="step-badge">1</span>
                    <span class="section-title">Selecione o Tipo de Cadastro</span>
                  </div>
                  <div class="section-content">
                    <CadastroSelector
                      v-model="selectedTipo"
                      :disabled="isProcessing"
                      @update:model-value="onTipoSelected"
                    />
                  </div>
                </div>

                <!-- Card 2: Requisitos do Arquivo -->
                <div class="section-card">
                  <div class="section-header">
                    <span class="step-badge">2</span>
                    <span class="section-title">Requisitos do Arquivo</span>
                  </div>
                  <div class="section-content">
                    <!-- Colunas obrigatórias -->
                    <div v-if="selectedConfig" class="requirements-info">
                      <v-icon size="18" color="info" class="mr-2">mdi-information</v-icon>
                      <span class="requirements-label">
                        Colunas Obrigatórias para {{ selectedConfig.label }}:
                      </span>
                    </div>
                    <div v-if="selectedConfig" class="chips-container">
                      <v-chip
                        v-for="campo in selectedConfig.camposObrigatorios"
                        :key="campo"
                        size="small"
                        variant="outlined"
                        class="chip-field"
                      >
                        {{ campo }}
                      </v-chip>
                    </div>
                    <div v-else class="text-grey text-body-2">
                      Selecione um tipo de cadastro para ver os requisitos.
                    </div>

                    <!-- Observações de campos condicionais (ex.: mod_trib) -->
                    <div v-if="notasCamposVisiveis.length > 0" class="mt-1 mb-2">
                      <div
                        v-for="nota in notasCamposVisiveis"
                        :key="nota.campo"
                        class="d-flex align-start text-caption text-medium-emphasis mb-1"
                      >
                        <v-icon size="14" color="info" class="mr-1 mt-1">mdi-information-outline</v-icon>
                        <span><strong>{{ nota.campo }}</strong> — {{ nota.texto }}</span>
                      </div>
                    </div>

                    <!-- Template download -->
                    <div class="template-section">
                      <span class="template-text">
                        Use nosso modelo para evitar erros de importação.
                      </span>
                      <v-btn
                        :href="selectedTipo && selectedTipo !== 'ativos' && selectedTipo !== 'produtos' ? getTemplateUrl : undefined"
                        :download="selectedTipo && selectedTipo !== 'ativos' && selectedTipo !== 'produtos' ? true : undefined"
                        :disabled="!selectedTipo || baixandoTemplate"
                        :loading="baixandoTemplate"
                        variant="outlined"
                        color="primary"
                        class="template-btn"
                        @click="onBaixarTemplate"
                      >
                        <v-icon start size="18">mdi-download</v-icon>
                        Baixar Template Excel
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- Coluna Direita -->
              <v-col cols="12" lg="6">
                <!-- Card 3: Upload do Arquivo -->
                <div class="section-card">
                  <div class="section-header">
                    <span class="step-badge">3</span>
                    <span class="section-title">Upload do Arquivo</span>
                  </div>
                  <div class="section-content">
                    <FileUploader
                      :disabled="isProcessing || !selectedTipo"
                      :current-file="arquivo"
                      @file-selected="onFileSelected"
                      @file-removed="onFileRemoved"
                      @error="onFileError"
                    />

                    <!-- Erro de upload -->
                    <v-alert
                      v-if="uploadError"
                      type="warning"
                      variant="tonal"
                      density="compact"
                      closable
                      class="mt-3"
                      @click:close="uploadError = null"
                    >
                      {{ uploadError }}
                    </v-alert>

                    <!-- Progresso (envio/fila) -->
                    <ImportProgress
                      v-if="isProcessing"
                      :processados="processados"
                      :total="totalParaProcessar"
                      :status="status"
                      class="mt-4"
                    />

                    <!-- Aviso: a validação ocorre durante o processamento -->
                    <v-alert
                      v-if="status === 'validated'"
                      type="info"
                      variant="tonal"
                      density="compact"
                      class="mt-4"
                    >
                      <v-alert-title class="text-body-2 font-weight-medium">Pronto para importar</v-alert-title>
                      Os registros são validados durante o processamento. Os que tiverem problemas
                      aparecerão para você corrigir e reenviar ao final.
                    </v-alert>

                    <!-- Botão Processar -->
                    <v-btn
                      color="primary"
                      size="large"
                      block
                      :loading="['queued', 'processing', 'importing'].includes(status)"
                      :disabled="!podeProcessar"
                      class="mt-4 process-btn"
                      @click="iniciarImportacao"
                    >
                      Processar Importação
                      <v-icon end>mdi-arrow-right</v-icon>
                    </v-btn>

                    <!-- Dica -->
                    <div class="tip-box mt-4">
                      <v-icon size="20" color="#F59E0B" class="mr-2">mdi-lightbulb</v-icon>
                      <div>
                        <span class="tip-label">Dica:</span>
                        <span class="tip-text">
                          Certifique-se de que a primeira linha do seu Excel
                          contém exatamente os nomes das colunas listadas no passo 2.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </template>
        </template>

        <!-- Snackbar para notificações -->
        <v-snackbar
          v-model="snackbar"
          :color="snackbarColor"
          :timeout="4000"
          location="bottom"
        >
          {{ snackbarText }}
          <template #actions>
            <v-btn variant="text" @click="snackbar = false">Fechar</v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<style scoped lang="scss">
.app-background {
  background-color: #F1F5F9 !important;
}

.header-section {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon {
  background: #2563EB;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 24px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 4px 0;
}

.header-subtitle {
  font-size: 14px;
  color: #64748B;
  margin: 0;
}

.section-card {
  background: #FFFFFF;
  border-radius: 16px;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #F1F5F9;
}

.step-badge {
  background: #2563EB;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1E293B;
}

.section-content {
  padding: 20px;
}

.requirements-info {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.requirements-label {
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.chip-field {
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 12px;
  border-color: #E2E8F0 !important;
  color: #475569 !important;
}

.template-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #F1F5F9;
  flex-wrap: wrap;
}

.template-text {
  font-size: 13px;
  color: #64748B;
  flex: 1;
  min-width: 200px;
}

.template-btn {
  text-transform: none;
  font-weight: 500;
}

.process-btn {
  text-transform: none;
  font-weight: 600;
  font-size: 16px;
  height: 52px;
  border-radius: 12px;
}

.tip-box {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  background: #FFFBEB;
  border: 1px solid #FEF3C7;
  border-radius: 12px;
}

.tip-label {
  font-weight: 600;
  color: #92400E;
  margin-right: 4px;
}

.tip-text {
  font-size: 13px;
  color: #92400E;
  line-height: 1.4;
}

@media (max-width: 960px) {
  .header-title {
    font-size: 20px;
  }

  .template-section {
    flex-direction: column;
    align-items: flex-start;
  }

  .template-btn {
    width: 100%;
  }
}
</style>
